import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import LaunchOfferBanner from "@/components/LaunchOfferBanner";

// Public pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";

// Protected pages
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectWizard from "@/pages/ProjectWizard";
import ShopsyBatch from "@/pages/ShopsyBatch";
import ShopsyBatch10s from "@/pages/ShopsyBatch10s";
import ShopsyBatchV3 from "@/pages/ShopsyBatchV3";
import AdminPanel from "@/pages/AdminPanel";
import TemplateManagement from "@/pages/TemplateManagement";
import TemplateCreator from "@/pages/TemplateCreator";
import TemplateGallery from "@/pages/TemplateGallery";
import TemplateDetail from "@/pages/TemplateDetail";
import MyRecreations from "@/pages/MyRecreations";
import Credits from "@/pages/Credits";
import Pricing from "@/pages/Pricing";
import CreditPricing from "@/pages/CreditPricing";
import ContactUs from "@/pages/ContactUs";
import ContactPage from "@/pages/ContactPage";
import ContactSubmissions from "@/pages/ContactSubmissions";
import AboutUs from "@/pages/AboutUs";
import TermsOfUse from "@/pages/TermsOfUse";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import RefundPolicy from "@/pages/RefundPolicy";
import PricingPublic from "@/pages/PricingPublic";
import Photoshoot from "@/pages/Photoshoot";
import PhotoshootAdmin from "@/pages/PhotoshootAdmin";
import PhotoshootGallery from "@/pages/PhotoshootGallery";
import Profile from "@/pages/Profile";
import AvatarStudio from "@/pages/AvatarStudio";
import ProductStudio from "@/pages/ProductStudio";
import MarketingStudio from "@/pages/MarketingStudio";
import ImageStudio from "@/pages/ImageStudio";
import ImageStudioPricing from "@/pages/ImageStudioPricing";

function ProtectedWithSidebar({ children }) {
  return (
    <ProtectedRoute>
      <DashboardLayout>{children}</DashboardLayout>
    </ProtectedRoute>
  );
}

// Fires a Meta Pixel PageView on SPA route changes (base pixel covers the initial load)
function PixelRouteTracker() {
  const location = useLocation();
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (window.fbq) window.fbq("track", "PageView");
  }, [location.pathname]);
  return null;
}

// Only shows the launch offer banner on public marketing/auth pages
const PUBLIC_BANNER_PATHS = new Set([
  "/",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/contact",
  "/book-demo",
  "/about",
  "/pricing-info",
]);

function PublicOnlyLaunchBanner() {
  const location = useLocation();
  if (!PUBLIC_BANNER_PATHS.has(location.pathname)) return null;
  return <LaunchOfferBanner />;
}

// Router component to handle session_id detection
function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/book-demo" element={<ContactUs />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/terms" element={<TermsOfUse />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/pricing-info" element={<PricingPublic />} />

      {/* Protected routes with sidebar */}
      <Route path="/dashboard" element={<ProtectedWithSidebar><Dashboard /></ProtectedWithSidebar>} />
      <Route path="/projects" element={<ProtectedWithSidebar><Projects /></ProtectedWithSidebar>} />
      <Route path="/project/:projectId" element={<ProtectedWithSidebar><ProjectWizard /></ProtectedWithSidebar>} />
      <Route path="/shopsy-batch" element={<ProtectedWithSidebar><ShopsyBatch /></ProtectedWithSidebar>} />
      <Route path="/shopsy-batch-10s" element={<ProtectedWithSidebar><ShopsyBatch10s /></ProtectedWithSidebar>} />
      <Route path="/shopsy-batch-v3" element={<ProtectedWithSidebar><ShopsyBatchV3 /></ProtectedWithSidebar>} />
      <Route path="/admin" element={<ProtectedWithSidebar><AdminPanel /></ProtectedWithSidebar>} />
      <Route path="/admin/templates" element={<ProtectedWithSidebar><TemplateManagement /></ProtectedWithSidebar>} />
      <Route path="/admin/templates/create" element={<ProtectedWithSidebar><TemplateCreator /></ProtectedWithSidebar>} />
      <Route path="/admin/templates/:templateId" element={<ProtectedWithSidebar><TemplateDetail /></ProtectedWithSidebar>} />
      <Route path="/admin/templates/:templateId/edit" element={<ProtectedWithSidebar><TemplateCreator /></ProtectedWithSidebar>} />
      <Route path="/templates" element={<ProtectedWithSidebar><TemplateGallery /></ProtectedWithSidebar>} />
      <Route path="/photoshoot" element={<ProtectedWithSidebar><Photoshoot /></ProtectedWithSidebar>} />
      <Route path="/photoshoot/gallery" element={<ProtectedWithSidebar><PhotoshootGallery /></ProtectedWithSidebar>} />
      <Route path="/my-recreations" element={<ProtectedWithSidebar><MyRecreations /></ProtectedWithSidebar>} />
      <Route path="/my-recreations/:recreationId" element={<ProtectedWithSidebar><MyRecreations /></ProtectedWithSidebar>} />
      <Route path="/credits" element={<ProtectedWithSidebar><Credits /></ProtectedWithSidebar>} />
      <Route path="/pricing" element={<ProtectedWithSidebar><Pricing /></ProtectedWithSidebar>} />
      <Route path="/profile" element={<ProtectedWithSidebar><Profile /></ProtectedWithSidebar>} />
      <Route path="/admin/credit-pricing" element={<ProtectedWithSidebar><CreditPricing /></ProtectedWithSidebar>} />
      <Route path="/admin/contact-submissions" element={<ProtectedWithSidebar><ContactSubmissions /></ProtectedWithSidebar>} />
      <Route path="/admin/photoshoot-templates" element={<ProtectedWithSidebar><PhotoshootAdmin /></ProtectedWithSidebar>} />
      <Route path="/avatar-studio" element={<ProtectedWithSidebar><AvatarStudio /></ProtectedWithSidebar>} />
      <Route path="/product-studio" element={<ProtectedWithSidebar><ProductStudio /></ProtectedWithSidebar>} />
      <Route path="/marketing-studio" element={<ProtectedWithSidebar><MarketingStudio /></ProtectedWithSidebar>} />
      <Route path="/image-studio" element={<ProtectedWithSidebar><ImageStudio /></ProtectedWithSidebar>} />
      <Route path="/admin/image-studio-pricing" element={<ProtectedWithSidebar><ImageStudioPricing /></ProtectedWithSidebar>} />
      <Route path="/admin/avatar-management" element={<ProtectedWithSidebar><AvatarStudio /></ProtectedWithSidebar>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <PixelRouteTracker />
        <PublicOnlyLaunchBanner />
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
